import Vue from 'vue';
import { PlantPerformanceFormStep } from '@/types/forms/formSteps/plantPerformanceFormStep';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import {
    isRequestWithoutPlanning,
    isTotalPlantPerformanceBelow300,
} from '../common';
import { toggleRequiredValidator } from '@/utilities/toggleRequiredValidator';
import { powerMatchesConnectionRules } from '@/utilities/utils';
import { getOverallActivePowerWithPreExisting } from '@/services/form/utilities/application/powerConnection';
import { GeneratorDataContentSearchByManufacturer } from '@/api/interfaces/content/form/serviceDriven/generatorDataContentSearchByManufacturer';
import { GeneratorDataContent } from '@/api/interfaces/content/form/serviceDriven/generatorDataContent';
import { contentServices } from '@/services/contentService';

function getGenerators(
    data: GeneratorDataContentSearchByManufacturer,
): GeneratorDataContent[] | undefined {
    const content = contentServices.form.content;
    if (!content) {
        return undefined;
    }

    const generatorsContent = content?.serviceDrivenContent?.generatorsContent;
    if (!generatorsContent) {
        return undefined;
    }

    return generatorsContent.filter(
        (generator) => generator.manufacturerName === data.manufacturerName,
    );
}

export const plantPerformanceGeneratorRules = async (
    formStep: {
        model: PlantPerformanceFormStep;
        formModel: ApplicationForm;
    } & Vue,
): Promise<void> => {
    const hasTotalPlantPerformanceBelow300 = isTotalPlantPerformanceBelow300(
        formStep.formModel,
    );
    const requestWithoutPlanning = isRequestWithoutPlanning(formStep.formModel);

    const overallActivePowerWithPreexisting =
        getOverallActivePowerWithPreExisting(
            formStep.formModel.applicationType,
            formStep.model,
            formStep.formModel.powerConnection,
        ) ?? 0;

    for (const module of formStep.model.generator.generatorModules.items) {
        module.generatorManualInputManufacturerTypeZerezId.disabled = requestWithoutPlanning;
        const enableManualInput =
            !module.generatorManualInputManufacturerTypeZerezId.disabled &&
            module.generatorManualInputManufacturerTypeZerezId.value === true;

        module.generatorActivePowerAndApparentPower.activePower.userChangeable =
            enableManualInput || requestWithoutPlanning;
        module.generatorActivePowerAndApparentPower.apparentPower.userChangeable =
            enableManualInput || requestWithoutPlanning;

        module.generatorManufacturerAndModuleType.manufacturer.disabled = requestWithoutPlanning;
        module.generatorManufacturerAndModuleType.moduleType.disabled = requestWithoutPlanning;
        module.generatorManufacturerAndModuleType.manufacturerText.disabled =
            requestWithoutPlanning || !enableManualInput;
        module.generatorManufacturerAndModuleType.moduleTypeText.disabled =
            requestWithoutPlanning || !enableManualInput;
        module.generatorManufacturerAndModuleType.manufacturerSelect.disabled =
            enableManualInput || requestWithoutPlanning;
        module.generatorManufacturerAndModuleType.moduleTypeSelect.disabled =
            enableManualInput || requestWithoutPlanning;
        module.generatorManufacturerAndModuleType.disabled =
            module.generatorManufacturerAndModuleType.manufacturer.disabled &&
            module.generatorManufacturerAndModuleType.moduleType.disabled;

        module.generatorZerezIdAndNorm.zerezId.disabled =
            enableManualInput || requestWithoutPlanning;
        module.generatorZerezIdAndNorm.norm.disabled =
            enableManualInput || requestWithoutPlanning;
        module.generatorZerezIdAndNorm.normText.disabled =
            enableManualInput || requestWithoutPlanning;
        module.generatorZerezIdAndNorm.disabled =
            module.generatorZerezIdAndNorm.zerezId.disabled &&
            module.generatorZerezIdAndNorm.norm.disabled &&
            module.generatorZerezIdAndNorm.normText.disabled;

        // Make module type select not-user-editable, if no manufacturer is selected
        module.generatorManufacturerAndModuleType.moduleTypeSelect.userChangeable = !!module
            .generatorManufacturerAndModuleType.manufacturerSelect.value;

        // Disable module type options that are not supported by the selected manufacturer
        if (
            !enableManualInput &&
            module.generatorManufacturerAndModuleType.manufacturerSelect.value
        ) {
            const generators = getGenerators({
                manufacturerName:
                    module.generatorManufacturerAndModuleType.manufacturerSelect
                        .value,
            });
            if (
                generators &&
                module.generatorManufacturerAndModuleType.moduleTypeSelect
                    .options
            ) {
                for (const option of module.generatorManufacturerAndModuleType
                    .moduleTypeSelect.options) {
                    option.disabled = !generators.some(
                        (generators) => generators.moduleType === option.key,
                    );
                }
            }
        }

        toggleRequiredValidator(
            module.generatorZerezIdAndNorm.norm,
            !enableManualInput,
        );
        toggleRequiredValidator(
            module.generatorZerezIdAndNorm.normText,
            !enableManualInput,
        );

        const dependantFieldsHaveValues =
            module.generatorManufacturerAndModuleType.manufacturer.value &&
            module.generatorManufacturerAndModuleType.moduleType.value &&
            module.generatorZerezIdAndNorm.zerezId.value;
        module.unitCertificateInvalidForInstalledPower.disabled = !(
            // We only need this error message, if the required fields for it are themselves filled in the first place
            (
                dependantFieldsHaveValues &&
                // Input is based on selection, instead of free text input
                !enableManualInput &&
                // Power check match, based on which connections the selected inverter supports
                !powerMatchesConnectionRules(
                    overallActivePowerWithPreexisting,
                    module.generatorZerezIdAndNorm.norm?.value ?? [],
                )
            )
        );

        const generatorTotalActivePower =
            module.generatorTotalActivePower.value ?? 0;
        const generatorTotalApparentPower =
            module.generatorTotalApparentPower.value ?? 0;

        // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573576/Feldgruppe+Generatoren+nicht+PV+oder+Speicher+NS#FeldgruppeGeneratoren(nichtPVoderSpeicher)NS-totalActivePower
        // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573576/Feldgruppe+Generatoren+nicht+PV+oder+Speicher+NS#FeldgruppeGeneratoren(nichtPVoderSpeicher)NS-totalApparentPower
        module.generatorTotalActivePowerIsGreaterThanTotalApparentPowerWarning.disabled =
            !hasTotalPlantPerformanceBelow300 ||
            generatorTotalActivePower <= generatorTotalApparentPower;
    }
};
